import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { CheckSquare, Check, ChevronRight, Inbox, BellRing, CalendarClock } from 'lucide-react'
import { useAppStore } from '../../stores/appStore'
import { approvalService, MODULE_STYLE, waitingLabel, resolveApprovalUrl } from '../../services/approvalService'
import { dashboardService } from '../../services/dashboardService'
import { formatCurrency, titleCaseCompany } from '../../utils/formatters'
import { toast } from '../../stores/uiStore'

/** API `target_url` boş/eksikse modül+id'den deeplink üret. */
function resolveTargetUrl(it) {
  return resolveApprovalUrl(it)
}

// Hatırlatma türü → rozet. Sunucudaki `kind` değerleriyle birebir
// (followup_reminder_service: rule_key + takvim kalem türleri).
const REMINDER_STYLE = {
  quote_idle:        { label: 'Teklif',  chip: 'bg-blue-50 text-blue-700 border-blue-100' },
  quote_order_track: { label: 'Sipariş', chip: 'bg-violet-50 text-violet-700 border-violet-100' },
  task_due:          { label: 'Görev',   chip: 'bg-amber-50 text-amber-700 border-amber-100' },
  task_reminder:     { label: 'Görev',   chip: 'bg-amber-50 text-amber-700 border-amber-100' },
  prospect:          { label: 'Aday',    chip: 'bg-teal-50 text-teal-700 border-teal-100' },
}

/** "Bugün 14:30" · "Yarın" · "2 gün gecikti" — gün farkı okunur olsun. */
function dueLabel(iso, overdue) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate())
  const days = Math.round((startOf(d) - startOf(new Date())) / 86400000)
  const hhmm = iso.slice(11, 16)
  const clock = hhmm && hhmm !== '00:00' ? ` ${hhmm}` : ''
  if (overdue) return days === 0 ? `Bugün${clock}` : `${Math.abs(days)} gün gecikti`
  if (days === 0) return `Bugün${clock}`
  if (days === 1) return `Yarın${clock}`
  return `${days} gün sonra`
}

/**
 * Header "Bekleyen Onaylar" rozeti + hızlı onay açılır penceresi.
 *
 * Rozet SAYAÇ ucundan (`/approvals/count`) beslenir — 60 sn'de bir, gövde
 * taşımadan. Liste yalnız kullanıcı açılır pencereyi AÇTIĞINDA çekilir.
 *
 * ⚠️ Açılır pencere `createPortal` ile body'ye render edilir — header
 * `overflow`/`z-index` bağlamında absolute menü kırpılıyor.
 */
export default function PendingApprovalsButton() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const activeCompany = useAppStore((s) => s.activeCompany)
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('approvals')   // 'approvals' | 'reminders'
  const btnRef = useRef(null)
  const popRef = useRef(null)
  const [pos, setPos] = useState({ top: 0, right: 0 })

  const { data: countData } = useQuery({
    queryKey: ['approvals-count', activeCompany],
    queryFn: () => approvalService.getCount(activeCompany),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
  const total = countData?.data?.total || 0

  const { data: listData, isLoading } = useQuery({
    queryKey: ['approvals-pending', 'dropdown', activeCompany],
    queryFn: () => approvalService.getPending(activeCompany, {}),
    enabled: open,
    staleTime: 15_000,
  })
  const items = (listData?.data?.items || []).slice(0, 6)

  // Hatırlatmalar YALNIZ sekmesi açıkken çekilir — rozet onay sayacından gelir,
  // her kullanıcıya dakikada bir ikinci liste indirmenin anlamı yok.
  const { data: remData, isLoading: remLoading } = useQuery({
    queryKey: ['my-reminders', 'dropdown', activeCompany],
    queryFn: () => dashboardService.getReminders(activeCompany, { days: 14, limit: 20 }),
    enabled: open && tab === 'reminders',
    staleTime: 60_000,
  })
  const reminders = remData?.data?.items || []
  const remTotal = remData?.data?.total || 0

  const approve = useMutation({
    mutationFn: (key) => approvalService.act(activeCompany, [key], 'approve'),
    onSuccess: () => {
      toast.success('Onaylandı')
      qc.invalidateQueries({ queryKey: ['approvals-count'] })
      qc.invalidateQueries({ queryKey: ['approvals-pending'] })
    },
    onError: (e) => toast.error('Onaylanamadı', e.response?.data?.detail || e.message),
  })

  const openItem = (it) => {
    setOpen(false)
    navigate(resolveTargetUrl(it))
  }

  useEffect(() => {
    if (!open || !btnRef.current) return
    const r = btnRef.current.getBoundingClientRect()
    setPos({ top: r.bottom + 8, right: Math.max(window.innerWidth - r.right, 8) })
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey = (e) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        title="Onaylar ve hatırlatmalar"
        className="relative p-2 hover:bg-surface-100 rounded-lg transition-colors"
      >
        <CheckSquare size={20} className="text-text-secondary" />
        {total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-amber-500 text-white
                           text-[10px] font-bold rounded-full flex items-center justify-center">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={popRef}
          style={{ top: pos.top, right: pos.right }}
          className="fixed z-50 w-[min(420px,calc(100vw-16px))] bg-white rounded-2xl border border-gray-100
                     shadow-[0_16px_48px_-12px_rgba(15,23,42,.25)] overflow-hidden"
        >
          {/* Onaylar ↔ Hatırlatmalar — iki liste ayrı ekran hak etmiyor, aynı
              pencerede sekme olarak duruyor. */}
          <div className="flex items-center gap-1 px-2 pt-2 pb-1 border-b border-gray-100">
            {[
              { id: 'approvals', label: 'Onaylar', icon: CheckSquare, count: total },
              { id: 'reminders', label: 'Hatırlatmalar', icon: BellRing, count: remTotal },
            ].map((t) => {
              const on = tab === t.id
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs
                              font-semibold transition-colors ${on
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  <t.icon size={14} />
                  {t.label}
                  {t.count > 0 && (
                    <span className={`min-w-[18px] px-1 rounded-full text-[10px] leading-4
                                      ${on ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {t.count > 99 ? '99+' : t.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            {tab === 'reminders' ? (
              remLoading ? (
                [...Array(3)].map((_, i) => (
                  <div key={i} className="px-4 py-3 border-b border-gray-50 animate-pulse">
                    <div className="h-3 w-44 bg-gray-200 rounded mb-2" />
                    <div className="h-3 w-20 bg-gray-100 rounded" />
                  </div>
                ))
              ) : reminders.length === 0 ? (
                <div className="py-10 text-center">
                  <CalendarClock size={26} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">Yaklaşan hatırlatma yok</p>
                </div>
              ) : (
                reminders.map((r, i) => {
                  const st = REMINDER_STYLE[r.kind] || { label: 'Hatırlatma', chip: 'bg-gray-50 text-gray-600 border-gray-200' }
                  return (
                    <button
                      key={`${r.kind}-${r.due_at}-${i}`}
                      type="button"
                      onClick={() => { setOpen(false); if (r.deeplink) navigate(r.deeplink) }}
                      className="w-full text-left flex items-start gap-2 px-3 py-2.5 border-b border-gray-50
                                 hover:bg-violet-50/40"
                    >
                      <span className={`shrink-0 mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${st.chip}`}>
                        {st.label}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs font-medium text-gray-900 line-clamp-2">{r.title}</span>
                        <span className={`block text-[11px] mt-0.5 ${r.overdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                          {dueLabel(r.due_at, r.overdue)}
                        </span>
                      </span>
                    </button>
                  )
                })
              )
            ) : isLoading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="px-4 py-3 border-b border-gray-50 animate-pulse">
                  <div className="h-3 w-40 bg-gray-200 rounded mb-2" />
                  <div className="h-3 w-24 bg-gray-100 rounded" />
                </div>
              ))
            ) : items.length === 0 ? (
              <div className="py-10 text-center">
                <Inbox size={26} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">Onayınızı bekleyen kayıt yok</p>
              </div>
            ) : (
              items.map((it) => {
                const m = MODULE_STYLE[it.module] || MODULE_STYLE.quote
                const fin = it.financial_impact
                return (
                  <div key={it.key}
                    className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-50 hover:bg-violet-50/40">
                    <button
                      type="button"
                      onClick={() => openItem(it)}
                      title="Detaya git"
                      className="flex-1 min-w-0 text-left cursor-pointer rounded-lg -mx-1 px-1 py-0.5"
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${m.chip}`}>
                          {it.module_label || m.label}
                        </span>
                        <span className="text-xs font-medium text-gray-900 truncate">
                          {it.title_code ? (
                            <span className="font-mono">{it.title_code}</span>
                          ) : null}
                          {it.title_code && (it.title_name || it.title) ? ' · ' : ''}
                          {titleCaseCompany(it.title_name) || (!it.title_code ? it.title : '')}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 truncate mt-0.5">
                        {titleCaseCompany(it.requester_name)} · {waitingLabel(it.age_days)} bekliyor
                        {fin?.amount != null && ` · ${formatCurrency(fin.amount, fin.currency)}`}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); approve.mutate(it.key) }}
                      disabled={approve.isPending}
                      title="Hızlı onayla"
                      className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100
                                 hover:bg-emerald-100 disabled:opacity-50 shrink-0"
                    >
                      <Check size={14} />
                    </button>
                  </div>
                )
              })
            )}
          </div>

          <button
            type="button"
            onClick={() => { setOpen(false); navigate(tab === 'reminders' ? '/tasks/my?view=calendar' : '/approvals') }}
            className="w-full flex items-center justify-center gap-1 px-4 py-2.5 text-sm font-medium
                       text-primary-600 hover:bg-primary-50 border-t border-gray-100"
          >
            {tab === 'reminders' ? 'Takvimde gör' : "Onay Merkezi'ni aç"} <ChevronRight size={14} />
          </button>
        </div>,
        document.body,
      )}
    </>
  )
}
